// src/page/AdminPanel.jsx
import React, { useEffect, useState } from "react";

export default function AdminPanel() {
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/providers`);
        const data = await res.json();
        setListings(data);
      } catch (err) {
        console.error("Failed to fetch listings:", err);
      }
    };

    const fetchUsers = async () => {
      try {
        // ✅ New API route for PostgreSQL users
        const token = localStorage.getItem("token");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    Promise.all([fetchListings(), fetchUsers()]).finally(() => setLoading(false));
  }, []);

  const viewListing = (listing) => setSelectedListing(listing);

  const removeListing = async (id) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/providers/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setListings(prev => prev.filter(l => l.id !== id));
        alert("Listing deleted ✅");
      } else {
        alert("Failed: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting listing");
    }
  };

  // ---------------- Stats ----------------
  const totalListings = listings.length;
  const totalUsers = users.length; // users now fetched from DB
  const totalOwners = new Set(listings.map(l => l.ownerEmail)).size;
  const categoryCounts = listings.reduce((acc, l) => {
    acc[l.category] = (acc[l.category] || 0) + 1;
    return acc;
  }, {});

  if (loading)
    return <p style={{ textAlign: "center", marginTop: "40px", color: "#f9fafb" }}>Loading data...</p>;

  return (
    <div style={{ padding: "40px", fontFamily: "Arial, sans-serif", backgroundColor: "#111827", minHeight: "100vh" }}>
      <h1 style={{ textAlign: "center", marginBottom: "30px", color: "#facc15" }}>Admin Panel</h1>

      {/* Stats Card */}
      <div style={statsCardStyle}>
        <h2 style={statsTitleStyle}>Statistics</h2>
        <ul style={statsListStyle}>
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <li key={cat}><strong style={{ color: "#facc15" }}>{cat}:</strong> {count} listings</li>
          ))}
          <li><strong style={{ color: "#34d399" }}>Total Listings:</strong> {totalListings}</li>
          <li><strong style={{ color: "#34d399" }}>Total Owners:</strong> {totalOwners}</li>
          <li><strong style={{ color: "#34d399" }}>Total Users:</strong> {totalUsers}</li>
        </ul>
      </div>

      {/* Listings Table Card */}
      <div style={tableCardStyle}>
        <h2 style={tableTitleStyle}>All Listings</h2>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Owner</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Address</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {listings.map(l => (
              <tr key={l.id} style={{ backgroundColor: "#1f2937" }}>
                <td style={tdStyle}>{l.id}</td>
                <td style={tdStyle}>{l.ownerEmail}</td>
                <td style={tdStyle}>{l.category}</td>
                <td style={tdStyle}>{l.branches.map(b => b.address).join(", ")}</td>
                <td style={tdStyle}>
                  <button onClick={() => viewListing(l)} style={viewButtonStyle}>View</button>
                  <button
                    onClick={() => removeListing(l.id)}
                    style={{
                      ...removeButtonStyle,
                      cursor: currentUser?.admin ? "pointer" : "not-allowed",
                      opacity: currentUser?.admin ? 1 : 0.5,
                    }}
                    disabled={!currentUser?.admin} // disables click for non-admins
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {selectedListing && (
        <div style={modalOverlayStyle} onClick={() => setSelectedListing(null)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: "#facc15" }}>Listing JSON: {selectedListing.name}</h3>
            <pre style={{ maxHeight: "400px", overflowY: "auto", background: "#111827", padding: "15px", color: "#f9fafb" }}>
              {JSON.stringify(selectedListing, null, 2)}
            </pre>
            <button onClick={() => setSelectedListing(null)} style={closeButtonStyle}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ------------------- Styles ------------------- */

const statsCardStyle = {
  background: "#1f2937",
  borderRadius: "12px",
  padding: "25px",
  marginBottom: "30px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  textAlign: "center",
  color: "#f9fafb",
};

const statsTitleStyle = {
  marginBottom: "15px",
  color: "#f87171", // 🔴 Stats card title color
};

const statsListStyle = {
  listStyle: "none",
  paddingLeft: 0,
  lineHeight: "1.8",
  display: "inline-block",
  textAlign: "left",
  color: "#f9fafb",
};

const tableCardStyle = {
  background: "#111827",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  overflowX: "auto",
  color: "#f9fafb",
};

const tableTitleStyle = {
  marginBottom: "15px",
  color: "#38bdf8", // 🔵 Table title color
  textAlign: "center",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
  color: "#f9fafb",
};

const thStyle = {
  border: "1px solid #374151",
  padding: "10px",
  backgroundColor: "#1f2937",
  color: "#34d399", // 🟢 Table header color
  textAlign: "center",
};

const tdStyle = {
  border: "1px solid #374151",
  padding: "10px",
  textAlign: "center",
};

const viewButtonStyle = {
  padding: "6px 12px",
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  marginRight: "8px",
};

const removeButtonStyle = {
  padding: "6px 12px",
  backgroundColor: "#dc2626",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  background: "#1f2937",
  padding: "20px",
  borderRadius: "8px",
  maxWidth: "700px",
  width: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "#f9fafb",
};

const closeButtonStyle = {
  marginTop: "10px",
  padding: "6px 12px",
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};
